// セミナー・講演会 カレンダー表示
// 一覧カード（.js-schedule-item）を元に、表示中の月のカレンダーを生成する（scheduleFilter.js から呼ぶ）。
// 1件のカードが時間帯（.time）を複数持つ場合は、時間帯ごとにチップを出す。
//
// カードから読む情報:
//   data-date                  … 開催日（"YYYY-MM-DD"）。無いカードはカレンダーに出さない
//   data-classification        … 種別（web / lecture）→ チップの時間帯アイコン
//   .tag-list                  … ポップオーバーへそのまま複製。製品タグ（種別タグ以外）の色バリアントをチップ背景色に使う
//   hgroup（.subttl / .title） … セル内はメインタイトル（.title）のみ、ポップオーバーは前後キャプション込みで複製
//   .time                      … 時間帯（複数可）
//   .btn-circle-arrow          … 詳細ページのリンク先
//
// ポップオーバーは Popover API（popover 属性 + popovertarget）で開閉し、
// 開いた時にチップ直下へ JS で位置を合わせる（最上位レイヤーに出るため座標は絶対配置）。

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const WEEKDAY_NAMES = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'];
const TYPE_TAG_CLASSES = ['-seminar', '-lecture']; // label-tag の種別バリアント（製品タグの判定に使う）
const POPOVER_GAP = 12; // チップ下端とポップオーバーの間隔（吹き出しの矢印の高さ）

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

export const createScheduleCalendar = root => {
  const container = root.querySelector('.js-schedule-calendar');
  if (!container) return null;

  let openPopover = null;

  // ----- ポップオーバーの位置合わせ -----
  const positionPopover = (popover, chip) => {
    const chipRect = chip.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const bounds = container.getBoundingClientRect();
    const chipCenter = chipRect.left + chipRect.width / 2;

    // チップ中央に揃え、カレンダーの左右からはみ出す分は内側へ寄せる
    let minLeft = bounds.left;
    let maxLeft = bounds.right - popoverRect.width;
    if (maxLeft < minLeft) {
      // ポップオーバーがカレンダーより広い（SP）→ 画面中央に置く
      minLeft = (document.documentElement.clientWidth - popoverRect.width) / 2;
      maxLeft = minLeft;
    }
    const left = Math.min(Math.max(chipCenter - popoverRect.width / 2, minLeft), maxLeft);

    popover.style.insetInlineStart = `${left + window.scrollX}px`;
    popover.style.insetBlockStart = `${chipRect.bottom + POPOVER_GAP + window.scrollY}px`;
    popover.style.setProperty('--arrow-offset', `${chipCenter - left}px`);
  };

  const closePopover = () => {
    if (openPopover && openPopover.isConnected) openPopover.hidePopover();
    openPopover = null;
  };

  // toggle はバブリングしないためキャプチャで受ける
  container.addEventListener(
    'toggle',
    event => {
      const popover = event.target;
      if (event.newState === 'open') {
        openPopover = popover;
        const chip = container.querySelector(`[popovertarget="${popover.id}"]`);
        if (chip) positionPopover(popover, chip);
      } else if (openPopover === popover) {
        openPopover = null;
      }
    },
    true
  );
  window.addEventListener('resize', closePopover);
  container.addEventListener('scroll', closePopover); // SP の横スクロール

  // ----- カードから情報を取り出す -----
  const productColorClass = item => {
    const productTag = [...item.querySelectorAll('.tag-list > .label-tag')].find(
      tag => !TYPE_TAG_CLASSES.some(typeClass => tag.classList.contains(typeClass))
    );
    return productTag ? [...productTag.classList].find(name => name.startsWith('-')) || '' : '';
  };

  const buildChip = ({ item, time }, popoverId) => {
    const chip = el('button', 'event-chip');
    chip.type = 'button';
    chip.setAttribute('popovertarget', popoverId);
    if (item.dataset.classification) chip.classList.add(`-${item.dataset.classification}`);
    const colorClass = productColorClass(item);
    if (colorClass) chip.classList.add(colorClass);

    if (time) chip.append(el('span', 'time', time));
    const title = item.querySelector('.title');
    chip.append(el('span', 'title', title ? title.textContent.trim() : ''));
    return chip;
  };

  const buildPopover = ({ item, time }, popoverId, year, month, day) => {
    const popover = el('div', 'event-popover');
    popover.id = popoverId;
    popover.setAttribute('popover', '');

    const date = el('div', 'date');
    date.append(
      el('span', 'icon'),
      el('span', 'day', String(day)),
      el('span', 'weekday', WEEKDAY_NAMES[new Date(year, month - 1, day).getDay()])
    );

    const detail = el('div', 'detail');
    const tagList = item.querySelector('.tag-list');
    if (tagList) detail.append(tagList.cloneNode(true));
    const heading = item.querySelector('hgroup');
    if (heading) {
      const headingClone = heading.cloneNode(true);
      headingClone.classList.add('heading');
      detail.append(headingClone);
    }
    if (time) {
      const timeNode = el('p', 'time');
      timeNode.append(el('span', 'icon'), time);
      detail.append(timeNode);
    }
    // 日付と詳細（タグ〜時間帯）を横並びの1行にまとめ、ボタンはその行の外（下）に置く
    const info = el('div', 'info');
    info.append(date, detail);
    popover.append(info);

    const link = item.querySelector('.btn-circle-arrow');
    if (link) {
      const action = el('div', 'action');
      const anchor = el('a', 'btn-circle-arrow -pill');
      anchor.href = link.getAttribute('href');
      anchor.append(el('p', 'label', '詳しくはこちら'), el('div', 'icon'));
      action.append(anchor);
      popover.append(action);
    }

    return popover;
  };

  // ----- 月のグリッドを描画 -----
  // ym … "YYYY-MM"、items … 種別・製品の絞り込みを通過したカード
  const render = (ym, items) => {
    openPopover = null; // DOM ごと置き換えるので開いているものは消える

    const [year, month] = ym.split('-').map(Number);
    const entriesByDay = new Map();
    items.forEach(item => {
      const date = item.dataset.date || '';
      if (!date.startsWith(ym)) return;
      const day = Number(date.slice(8, 10));
      const times = [...item.querySelectorAll('.time')].map(node => node.textContent.trim());
      if (!entriesByDay.has(day)) entriesByDay.set(day, []);
      (times.length ? times : ['']).forEach(time => entriesByDay.get(day).push({ item, time }));
    });

    const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0 = 日曜
    const daysInMonth = new Date(year, month, 0).getDate();
    const daysInPrevMonth = new Date(year, month - 1, 0).getDate();
    const cellCount = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;

    const table = el('table', 'grid');
    table.setAttribute('aria-label', `${year}年${month}月のカレンダー`);

    const head = el('thead', 'weekdays');
    const headRow = el('tr', 'row');
    WEEKDAYS.forEach(name => {
      const weekday = el('th', 'weekday', name);
      weekday.scope = 'col';
      headRow.append(weekday);
    });
    head.append(headRow);

    const body = el('tbody', 'weeks');
    let row = null;
    let popoverIndex = 0;
    for (let index = 0; index < cellCount; index += 1) {
      if (index % 7 === 0) {
        row = el('tr', 'row');
        body.append(row);
      }
      const offset = index - firstWeekday; // 当月1日からのずれ
      const isCurrentMonth = offset >= 0 && offset < daysInMonth;
      const cell = el('td', isCurrentMonth ? 'cell' : 'cell -other');
      let dayNumber;
      if (offset < 0) {
        dayNumber = daysInPrevMonth + offset + 1;
      } else if (offset >= daysInMonth) {
        dayNumber = offset - daysInMonth + 1;
      } else {
        dayNumber = offset + 1;
      }
      cell.append(el('span', 'day', String(dayNumber)));

      const entries = isCurrentMonth ? entriesByDay.get(dayNumber) : null;
      if (entries) {
        const events = el('ul', 'events');
        entries.forEach(entry => {
          popoverIndex += 1;
          const popoverId = `schedule-event-${popoverIndex}`;
          const listItem = el('li', 'item');
          listItem.append(buildChip(entry, popoverId), buildPopover(entry, popoverId, year, month, dayNumber));
          events.append(listItem);
        });
        cell.append(events);
      }
      row.append(cell);
    }

    table.append(head, body);
    container.replaceChildren(table);
  };

  return { render };
};
